import React, {
  useState, useRef, useEffect, useMemo, useReducer, useSyncExternalStore,
} from 'react';
import ChatSoftGate from './ChatSoftGate';
import WhoPicker from './WhoPicker';
import { useSubscription } from '../../hooks/useSubscription';
import {
  type UsePlanChatReturn, type PlanChange, type PlanChatMessage, PENDING_PLAN_OWNER_KEY,
} from '../../hooks/usePlanChat';
import ProposalCard from './ProposalCard';
import PendingPlanPreview from './PendingPlanPreview';
import PendingDays, { PendingDaysRange } from './PendingDays';
import { CaptureButton, CaptureIdeasPanel, useCaptureView } from './CaptureIdeas';
import './ChatOverlay.css';
import { ReportContentModal } from '../common/ReportContentModal';
import { buildAiSnapshot } from '../../services/reportApi';
import { useUserStore } from '../../stores';
import { resolvePendingAnonSession } from '../../stores/useUserStore';
import { trackChatSoftGateHit, trackUpgradeCtaClicked } from '../../hooks/useAnalytics';

/** Where an anonymous build's plan waits for signup (`usePlanChat` writes it,
 *  `useUserStore` removes it only after a successful resolve), and which
 *  conversation built it. Read-only here. */
const PENDING_PLAN_KEY = 'vizday_pending_plan';
export const readPendingPlan = (): string | null => {
  try { return localStorage.getItem(PENDING_PLAN_KEY); } catch { return null; }
};
const readPendingPlanOwner = (): string | null => {
  try { return localStorage.getItem(PENDING_PLAN_OWNER_KEY); } catch { return null; }
};
/** Another tab's write reaches this one only as a `window` `storage` event.
 *  No key filter: the snapshots are two `getItem`s, and React re-renders only
 *  when a snapshot string changed. */
const subscribeStorage = (onChange: () => void): (() => void) => {
  window.addEventListener('storage', onChange);
  return () => window.removeEventListener('storage', onChange);
};
/** The stored plan, parsed; `undefined` for an unparseable string. */
const parsePendingPlan = (raw: string | null): unknown => {
  if (raw == null) return undefined;
  try { return JSON.parse(raw); } catch { return undefined; }
};

/** Render simple markdown: **bold**, - bullets, line breaks */
function renderMarkdown(text: string): React.ReactNode {
  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: React.ReactNode[] = [];

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul-${elements.length}`} className="chat-md-list">{listItems}</ul>);
      listItems = [];
    }
  };

  const formatInline = (line: string, key: string): React.ReactNode => {
    // Split on **bold** markers
    const parts = line.split(/(\*\*[^*]+\*\*)/g);
    return (
      <span key={key}>
        {parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
          }
          return part;
        })}
      </span>
    );
  };

  lines.forEach((line, i) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('- ') || trimmed.startsWith('• ')) {
      const content = trimmed.slice(2);
      listItems.push(<li key={`li-${i}`}>{formatInline(content, `li-c-${i}`)}</li>);
    } else {
      flushList();
      if (trimmed === '') {
        elements.push(<br key={`br-${i}`} />);
      } else {
        elements.push(<p key={`p-${i}`} className="chat-md-para">{formatInline(trimmed, `p-c-${i}`)}</p>);
      }
    }
  });
  flushList();

  return <>{elements}</>;
}

/**
 * The line under the build timer (Almanac decision 13). It ships only against
 * a measured wait: set to `'Usually about a minute.'` when the recorded
 * reading meets the rule (>= 50 authenticated first builds, p90 <= 75 s), and
 * `null` otherwise — the timer alone then speaks. No reading was taken for S8,
 * so it is `null`. The overlay takes it as a prop defaulting to this value, so
 * tests can render both cases.
 */
export const BUILD_WAIT_SENTENCE: string | null = null;

/** The timer stays hidden until this many seconds have passed (decision 13,
 *  option A): a quick reply never shows it. */
export const BUILD_TIMER_AFTER_S = 20;

/** Elapsed seconds as `m:ss`. */
export const formatElapsed = (seconds: number): string => {
  const whole = Math.max(0, Math.floor(seconds));
  return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, '0')}`;
};

/**
 * The honest wait while a reply or a build is in flight (fidelity decision 8,
 * P building()). A `Building` label from t=0 is drawn only when the caller can
 * prove the turn is a build, which no web caller can today (slice W3d waits on
 * a server signal), and the pending-day slots only when dates are known; on any
 * turn, from 20 s, the elapsed time counting up in mono, and the wait sentence
 * under it when there is one. No dots, no spinner, no invented progress steps.
 */
export const BuildWait: React.FC<{
  waitSentence?: string | null;
  building: boolean;
  dates: PendingDaysRange | null;
}> = ({
  waitSentence = BUILD_WAIT_SENTENCE,
  building,
  dates,
}) => {
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const started = Date.now();
    const timer = setInterval(() => setElapsed((Date.now() - started) / 1000), 1000);
    return () => clearInterval(timer);
  }, []);
  const showTimer = elapsed >= BUILD_TIMER_AFTER_S;
  return (
    <div className="build-wait-frame">
      {building && <span className="build-label">Building</span>}
      {building && <PendingDays dates={dates} />}
      {showTimer && (
        <div className="build-wait">
          <span className="build-wait-elapsed" data-testid="build-elapsed">{formatElapsed(elapsed)}</span>
          {typeof waitSentence === 'string' && (
            <span className="build-wait-sentence">{waitSentence}</span>
          )}
        </div>
      )}
    </div>
  );
};


/** A committed change, named the way the reader would name it. */
const changeVerb: Record<PlanChange['op'], string> = {
  add: 'Added', update: 'Updated', move: 'Moved', remove: 'Removed',
};

const changeDate = (iso?: string): string => {
  if (!iso) return '';
  const parsed = new Date(`${iso}T00:00:00`);
  return Number.isNaN(parsed.getTime())
    ? '' : parsed.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const PlanChangeList: React.FC<{ changes: PlanChange[] }> = ({ changes }) => (
  <ul className="chat-change-list">
    {changes.map((change, i) => {
      const when = changeDate(change.date);
      const fields = change.op === 'update' && change.fields?.length
        ? change.fields.join(', ') : '';
      // Index in the key: one batch may update the same item twice, so the
      // item id alone is not unique.
      return (
        <li key={`${change.op}-${change.item_id ?? 'new'}-${i}`} className="chat-change">
          <span className="chat-change-verb">{changeVerb[change.op] ?? 'Changed'}</span>
          <span className="chat-change-name">{change.name || 'an activity'}</span>
          {/* Independent of the date: a replayed pre-deploy receipt has fields
              but no date, and dropping them would show less than we know. */}
          {(when || fields) && (
            <span className="chat-change-when">
              {[when, fields].filter(Boolean).join(' \u00b7 ')}
            </span>
          )}
        </li>
      );
    })}
  </ul>
);

interface PlanChatOverlayProps {
  chat: UsePlanChatReturn;
  isExpanded: boolean;
  onClose: () => void;
  onNavigate?: (path: string) => void;
  /** When set to 'plan_edit', hide the invite action (editing existing plan) */
  chatType?: 'plan_create' | 'plan_edit';
  /** Identifies the itinerary when this overlay is editing an existing plan. */
  planName?: string;
  /** The line under the build timer; defaults to `BUILD_WAIT_SENTENCE`. */
  buildWaitSentence?: string | null;
}

const PlanChatOverlay: React.FC<PlanChatOverlayProps> = ({
  chat,
  isExpanded,
  onClose,
  onNavigate,
  chatType = 'plan_create',
  planName,
  buildWaitSentence = BUILD_WAIT_SENTENCE,
}) => {
  // Flagging AI output. This overlay answers anonymous visitors, which is
  // exactly the surface Google's AI-Generated Content policy is about.
  const [reportingMessageId, setReportingMessageId] = useState<string | null>(null);
  const { handleUpgrade } = useSubscription();
  const {
    messages,
    isSending,
    itineraryId,
    showUndo,
    softGate,
    error,
    sendMessage,
    undo,
    messagesEndRef,
    canRetry,
    retryLastMessage,
  } = chat;

  // The save bar (Almanac decision 13, option A): one bar saying the plan is
  // not kept until it is saved. It cannot be dismissed.
  //
  // Shown if and only if a pending plan is stored AND this conversation built
  // it: `usePlanChat` records the building chat's entry id beside the plan
  // (`PENDING_PLAN_OWNER_KEY`), so another conversation's plan never raises
  // this chat's bar, and a second build moves the bar to the chat that built
  // it. Read on every render, like the plan itself, through an external-store
  // subscription so a write from another tab re-renders this one too.
  //
  // It stays while the plan is stored, whatever the auth state: the plan key
  // is removed only by a successful resolve, so signing in is not saving.
  // Signed out, the button starts signup (signup IS the save — `useUserStore`
  // claims and resolves after auth, exactly as before). Signed in with the
  // plan still stored (a resolve failed), it calls that same resolve again.
  const user = useUserStore((s) => s.user);
  const storedPendingPlan = useSyncExternalStore(subscribeStorage, readPendingPlan);
  const pendingOwner = useSyncExternalStore(subscribeStorage, readPendingPlanOwner);
  const showSaveBar = chatType === 'plan_create' && storedPendingPlan != null
    && chat.entryId != null && pendingOwner === chat.entryId;
  // The gate's measurement: `chat_soft_gate_hit` once per gate activation,
  // counted here and only here. The card is told not to count its own mount
  // (`trackHit={false}` below), because on this surface the card and the save
  // bar take turns standing for one signup activation (card -> bar after a
  // build, bar -> card when the plan leaves storage, e.g. saved in another
  // tab), and a mount-time count would count that activation again. The
  // activation is `softGate.active` going false -> true, any gate type — what
  // the card's single mount used to count at the base.
  const signupGateActive = softGate.active && softGate.type === 'signup';
  const barStandsInForGate = showSaveBar && signupGateActive;
  const gateActive = softGate.active;
  const gateActivationCountedRef = useRef(false);
  useEffect(() => {
    if (!gateActive) {
      gateActivationCountedRef.current = false;
      return;
    }
    if (gateActivationCountedRef.current) return;
    trackChatSoftGateHit(0);
    gateActivationCountedRef.current = true;
  }, [gateActive]);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'failed'>('idle');
  const saveMountedRef = useRef(true);
  useEffect(() => {
    saveMountedRef.current = true;
    return () => { saveMountedRef.current = false; };
  }, []);
  // The preview is what Save saves: it renders the stored plan under the save
  // bar's own predicate. Parsed once per distinct stored string.
  const pendingPlan = useMemo(() => parsePendingPlan(storedPendingPlan), [storedPendingPlan]);
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const handleSave = async () => {
    // Stale at Save: storage changed after this render (another tab's write
    // whose event has not landed yet). The resolver sends whatever is stored,
    // so a click now would save a plan the visitor has not seen. Neither
    // track, navigate nor resolve; re-render, so the plan Save would send is
    // on screen, and the visitor clicks again.
    if (readPendingPlan() !== storedPendingPlan || readPendingPlanOwner() !== pendingOwner) {
      forceRender();
      return;
    }
    trackUpgradeCtaClicked(barStandsInForGate ? 'chat_soft_gate_signup' : 'save_bar');
    if (!user) {
      onNavigate?.('/signup');
      return;
    }
    setSaveState('saving');
    // The resolve reports failure by keeping the plan stored.
    await resolvePendingAnonSession(user);
    if (!saveMountedRef.current) return;
    setSaveState(readPendingPlan() != null ? 'failed' : 'idle');
  };

  const [inputValue, setInputValue] = useState('');
  // Inviting is opt-in via the "Invite people" action — solo is the silent
  // default, and the picker never blocks the conversation.
  const [showInvite, setShowInvite] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  // Plan from a photo: where the ideas panel sits in the transcript. It is
  // anchored after the last message on screen when its view last changed (a
  // prompt opened, a capture started, failed or finished), so it lands
  // nearest the "Add a photo" button that opened it, and every message added
  // after that renders BELOW it. "Plan it" keeps the list and resets the
  // chat: the anchor message is gone, so the whole new transcript — the
  // turn, its progress, reply, error or gate — follows the panel, where the
  // end-anchored auto-scroll below reveals it. Anchored by id, not count: a
  // reset and the next send can land in one render. Never in an edit chat —
  // a capture always starts a new plan.
  const showCapture = chatType !== 'plan_edit';
  const captureView = useCaptureView();
  const lastMessageId = messages.length ? messages[messages.length - 1].id : null;
  const [captureAnchor, setCaptureAnchor] = useState({ view: captureView, afterId: lastMessageId });
  let anchorAfterId = captureAnchor.afterId;
  if (captureAnchor.view !== captureView) {
    anchorAfterId = lastMessageId;
    setCaptureAnchor({ view: captureView, afterId: anchorAfterId });
  }
  // One past the anchor message; 0 when it is gone (a reset) or there was none.
  const captureAt = anchorAfterId == null
    ? 0 : messages.findIndex((m) => m.id === anchorAfterId) + 1;

  // Auto-scroll to the end of the transcript on anything new there: a
  // message, progress, and the tail content that can appear with no message
  // (an error, the invite picker opening, a soft gate). The tail is always
  // last, so the end is where it is.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending, error, showInvite, softGate.active, messagesEndRef]);

  // Focus input when expanded
  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => inputRef.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isSending) return;
    sendMessage(inputValue.trim());
    setInputValue('');
  };

  const handleUndo = async () => {
    const result = await undo();
    if (result.deleted) {
      onNavigate?.('/plans');
    }
  };

  const handleViewPlan = () => {
    if (itineraryId) {
      onNavigate?.(`/itinerary/${itineraryId}`);
    }
  };

  const handleQuickAction = (text: string) => {
    sendMessage(text);
  };

  const renderMessage = (message: PlanChatMessage) => (
    <div key={message.id} className={`chat-message ${message.role}`}>
      {message.role === 'assistant' && (
        <div className="message-avatar">V</div>
      )}
      <div className="message-bubble">
        <div className="message-text">
          {message.role === 'assistant' ? renderMarkdown(message.content) : message.content}
        </div>
        {message.changes?.length ? (
          <PlanChangeList changes={message.changes} />
        ) : null}
        {message.proposalId ? (
          <ProposalCard
            proposalId={message.proposalId}
            onResolve={chat.resolveProposal}
          />
        ) : null}
        {message.role === 'assistant' && (
          <button
            type="button"
            className="message-report-btn"
            onClick={() => setReportingMessageId(String(message.id))}
            aria-label="Report this response"
          >
            Report
          </button>
        )}
      </div>
    </div>
  );

  // Quick actions, progress, error, invite and gate: the tail of the current
  // turn, always last — after the panel and every message — where the
  // auto-scroll above reveals it. The panel reveals itself on its own view
  // changes, wherever it sits.
  const tail = [
    // Quick actions for empty state
    messages.length === 0 && chatType === 'plan_edit' && (
      <div key="edit-intro" className="chat-message assistant">
        <div className="message-avatar">V</div>
        <div className="message-bubble">
          <div className="message-text">
            {`I'm editing ${planName || 'this plan'}. Tell me what to add, remove, move, or change.`}
          </div>
        </div>
      </div>
    ),

    messages.length === 0 && chatType === 'plan_create' && (
      <div key="quick-actions" className="quick-actions-section">
        <div className="quick-actions">
          <button
            className="quick-action-btn"
            onClick={() => handleQuickAction('Help me plan a trip')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
              <circle cx="12" cy="10" r="3"/>
            </svg>
            Plan a trip
          </button>
          <button
            className="quick-action-btn"
            onClick={() => handleQuickAction('What destinations are trending right now?')}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Trending destinations
          </button>
        </div>
      </div>
    ),

    // The build wait (decision 8): the frame shows nothing before 20 s on every
    // web turn, then the elapsed time. The `Building` label waits for a server
    // signal that the turn is a build (slice W3d, r1145): no web client state
    // can prove it, so `building` and `dates` are the seam W3d fills.
    isSending && (
      <div key="progress" className="chat-message assistant">
        <div className="message-avatar">V</div>
        <div className="message-bubble">
          <BuildWait
            waitSentence={buildWaitSentence}
            building={false}
            dates={null}
          />
        </div>
      </div>
    ),

    // Error
    error && (
      <div key="error" className="chat-message assistant chat-message--error">
        <div className="message-avatar">V</div>
        <div className="message-bubble">
          <div className="message-text">{error}</div>
        </div>
      </div>
    ),

    // One-tap retry (chat-build-fail-retry decision 8): the latest turn failed
    // on our side, so resend the last message rather than make the reader
    // retype it. After the error or failed reply, in normal flow.
    canRetry && retryLastMessage && (
      <div key="try-again" className="chat-try-again-row">
        <button
          type="button"
          className="chat-try-again"
          aria-label="Try again"
          disabled={isSending}
          onClick={retryLastMessage}
        >
          Try again
        </button>
      </div>
    ),

    // Invite picker — opened on demand from the action bar
    showInvite && itineraryId != null && (
      <WhoPicker
        key="invite"
        itineraryId={itineraryId}
        onAnswered={() => setShowInvite(false)}
      />
    ),

    // Soft gate. After an anonymous build the save bar is the one signup
    // CTA, so the signup card steps aside; the input stays gated either way.
    softGate.active && !(softGate.type === 'signup' && showSaveBar) && (
      <ChatSoftGate
        key="soft-gate"
        gateType={softGate.type as 'signup' | 'upgrade' | ''}
        onUpgrade={() => softGate.type === 'upgrade' ? handleUpgrade() : onNavigate?.('/login')}
        onDismiss={handleClose}
        trackHit={false}
      />
    ),
  ].filter(Boolean);
  const splitAt = showCapture ? captureAt : messages.length;
  const transcript = [
    ...messages.slice(0, splitAt).map(renderMessage),
    ...(showCapture
      ? [<CaptureIdeasPanel key="capture-panel" reset={chat.reset} onNavigate={onNavigate} />]
      : []),
    ...messages.slice(splitAt).map(renderMessage),
    // The unsaved plan itself, after the last message and before the tail,
    // directly above the save bar it shares a predicate with. A stored value
    // that is unparseable or not a plan renders no card while the bar shows.
    ...(showSaveBar ? [<PendingPlanPreview key="pending-plan" plan={pendingPlan} />] : []),
    ...tail,
  ];

  return (
    <div className={`home-chat-overlay ${isExpanded ? 'expanded' : ''} ${isClosing ? 'closing' : ''}`}>
      <div className="home-chat-panel" ref={panelRef}>
        {/* Header — no model picker */}
        <div className="home-chat-header">
          <div className="home-chat-header-left">
            <div className="home-chat-avatar">
              <span>V</span>
            </div>
            <div className="home-chat-header-text">
              <span className="home-chat-title">
                {chatType === 'plan_edit' ? `Edit ${planName || 'this plan'}` : 'VizDay Travel'}
              </span>
              <span className="home-chat-subtitle">
                {chatType === 'plan_edit' ? 'Changes apply to this itinerary' : 'Your travel planning agent'}
              </span>
            </div>
          </div>
          <button className="home-chat-close-btn" onClick={handleClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Messages */}
        <div className="home-chat-messages">
          {transcript}

          <div ref={messagesEndRef} />
        </div>

        {/* Action bar — fixed between messages and input */}
        {itineraryId != null && (chatType === 'plan_create' || showUndo) && (
          <div className="plan-chat-action-bar">
            {chatType === 'plan_create' && (
              <>
                <button className="plan-action-btn plan-action-view" onClick={handleViewPlan}>
                  View plan
                </button>
                <button className="plan-action-btn plan-action-view" onClick={() => setShowInvite(v => !v)}>
                  Invite people
                </button>
              </>
            )}
            {showUndo && (
              <button className="plan-action-btn plan-action-undo" onClick={handleUndo}>
                Undo
              </button>
            )}
          </div>
        )}

        {showSaveBar && (
          <div className="savebar" data-testid="savebar">
            <button
              type="button"
              className="savebar-btn"
              onClick={handleSave}
              disabled={saveState === 'saving'}
            >
              {saveState === 'saving' ? 'Saving…' : 'Save to my plans'}
            </button>
            {saveState === 'failed' && (
              <span className="savebar-error" role="alert">Couldn’t save. Try again.</span>
            )}
            {!user && (
              <span className="savebar-note">It’s only in this browser until you do. An account is free.</span>
            )}
          </div>
        )}

        {/* Input */}
        <form className="chat-input-area" onSubmit={handleSubmit}>
          {chatType !== 'plan_edit' && <CaptureButton disabled={isSending} active={isExpanded} />}
          <textarea
            ref={inputRef}
            rows={2}
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit(e);
              }
            }}
            placeholder={softGate.active
              ? (softGate.type === 'signup' ? 'Sign up to continue planning' : 'Upgrade to continue chatting')
              : chatType === 'plan_edit' ? 'What would you like to change?' : 'Where do you want to go?'}
            disabled={isSending || softGate.active}
          />
          <button
            type="submit"
            disabled={!inputValue.trim() || isSending || softGate.active}
            className="send-button"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
            </svg>
          </button>
        </form>
      </div>

      <ReportContentModal
        isOpen={reportingMessageId !== null}
        onClose={() => setReportingMessageId(null)}
        targetType="ai_message"
        targetId={reportingMessageId ?? ''}
        targetLabel="this AI response"
        context="plan chat"
        contentSnapshot={(() => {
          const m = messages.find((x) => String(x.id) === reportingMessageId);
          return buildAiSnapshot(m?.content ?? '', {
            changes: m?.changes,
            proposalId: m?.proposalId,
          });
        })()}
      />
    </div>
  );
};

export default PlanChatOverlay;
